import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isUserAdmin } from '../../../lib/supabase';
import { Search } from 'lucide-react';

interface MentorData {
  uuid: string;
  user_uuid: string;
  specialization: string;
  vincer_base_user: {
    name: string;
    email: string;
  };
}

interface MenteeData {
  uuid: string;
  user_uuid: string;
  vincer_base_user: {
    name: string;
    email: string;
  };
}

interface Mentor {
  uuid: string;
  user_uuid: string;
  name: string;
  email: string;
  specialization: string;
}

interface Mentee {
  uuid: string;
  user_uuid: string;
  name: string;
  email: string;
}

export function NewAssignment() {
  const navigate = useNavigate();
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [mentees, setMentees] = useState<Mentee[]>([]);
  const [selectedMentor, setSelectedMentor] = useState<string>('');
  const [selectedMentee, setSelectedMentee] = useState<string>('');
  const [mentorSearch, setMentorSearch] = useState('');
  const [menteeSearch, setMenteeSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showMentorDropdown, setShowMentorDropdown] = useState(false);
  const [showMenteeDropdown, setShowMenteeDropdown] = useState(false);

  useEffect(() => {
    async function checkAdminStatus() {
      const adminStatus = await isUserAdmin();
      setIsAdmin(adminStatus);
      if (!adminStatus) {
        setError('You must be an admin to create assignments');
      }
    }
    checkAdminStatus();
  }, []);

  useEffect(() => {
    async function fetchUsers() {
      try {
        // Fetch mentors
        const { data: mentorData, error: mentorError } = await supabase
          .from('mentor')
          .select(`
            uuid,
            user_uuid,
            specialization,
            vincer_base_user (
              name,
              email
            )
          `);

        if (mentorError) throw mentorError;

        // Fetch mentees
        const { data: menteeData, error: menteeError } = await supabase
          .from('mentee')
          .select(`
            uuid,
            user_uuid,
            vincer_base_user (
              name,
              email
            )
          `);

        if (menteeError) throw menteeError;

        const formattedMentors = (mentorData as unknown[] as { 
          uuid: string; 
          user_uuid: string; 
          specialization: string; 
          vincer_base_user: { name: string; email: string; } 
        }[]).map(mentor => ({
          uuid: mentor.uuid,
          user_uuid: mentor.user_uuid,
          name: mentor.vincer_base_user.name,
          email: mentor.vincer_base_user.email,
          specialization: mentor.specialization
        }));

        const formattedMentees = (menteeData as unknown[] as { 
          uuid: string; 
          user_uuid: string; 
          vincer_base_user: { name: string; email: string; } 
        }[]).map(mentee => ({
          uuid: mentee.uuid,
          user_uuid: mentee.user_uuid,
          name: mentee.vincer_base_user.name,
          email: mentee.vincer_base_user.email
        }));

        setMentors(formattedMentors);
        setMentees(formattedMentees);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, []);

  const filteredMentors = useMemo(() => {
    return mentors.filter(mentor =>
      mentor.name.toLowerCase().includes(mentorSearch.toLowerCase()) ||
      mentor.email.toLowerCase().includes(mentorSearch.toLowerCase()) ||
      mentor.specialization.toLowerCase().includes(mentorSearch.toLowerCase())
    );
  }, [mentors, mentorSearch]);

  const filteredMentees = useMemo(() => {
    return mentees.filter(mentee =>
      mentee.name.toLowerCase().includes(menteeSearch.toLowerCase()) ||
      mentee.email.toLowerCase().includes(menteeSearch.toLowerCase())
    );
  }, [mentees, menteeSearch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) {
      setError('You must be an admin to create assignments');
      return;
    }

    if (!selectedMentor || !selectedMentee) {
      setError('Please select both a mentor and a mentee');
      return;
    }

    try {
      const { error: assignmentError } = await supabase
        .from('relationship_assignments')
        .insert({
          uuid: crypto.randomUUID(),
          mentor_uuid: selectedMentor,
          mentee_uuid: selectedMentee,
          start: new Date().toISOString(),
          end: null // Can be updated later when the assignment ends
        });

      if (assignmentError) throw assignmentError;

      navigate('/coordinator/assignments');
    } catch (err) {
      console.error('Error creating assignment:', err);
      setError('Failed to create assignment');
    }
  };

  const getSelectedMentorName = () => {
    const mentor = mentors.find(m => m.uuid === selectedMentor);
    return mentor ? `${mentor.name} (${mentor.email})` : '';
  };

  const getSelectedMenteeName = () => {
    const mentee = mentees.find(m => m.uuid === selectedMentee);
    return mentee ? `${mentee.name} (${mentee.email})` : '';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Assign Mentor to Mentee</h1>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-sm">
        <div className="relative">
          <label htmlFor="mentor" className="block text-sm font-medium text-gray-700 mb-2">
            Select Mentor
          </label>
          <div className="relative">
            <input
              type="text"
              value={selectedMentor ? getSelectedMentorName() : mentorSearch}
              onChange={(e) => {
                setMentorSearch(e.target.value);
                setSelectedMentor('');
                setShowMentorDropdown(true);
              }}
              onFocus={() => setShowMentorDropdown(true)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
              placeholder="Search mentors..."
              disabled={!isAdmin}
            />
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
          {showMentorDropdown && mentorSearch && (
            <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border max-h-60 overflow-auto">
              {filteredMentors.map((mentor) => (
                <button
                  key={mentor.uuid}
                  type="button"
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:outline-none"
                  onClick={() => {
                    setSelectedMentor(mentor.uuid);
                    setMentorSearch('');
                    setShowMentorDropdown(false);
                  }}
                >
                  <div className="font-medium">{mentor.name}</div>
                  <div className="text-sm text-gray-500">{mentor.email}</div>
                  <div className="text-sm text-gray-400">{mentor.specialization}</div>
                </button>
              ))}
              {filteredMentors.length === 0 && (
                <div className="px-4 py-2 text-gray-500">No mentors found</div>
              )}
            </div>
          )}
        </div>

        <div className="relative">
          <label htmlFor="mentee" className="block text-sm font-medium text-gray-700 mb-2">
            Select Mentee
          </label>
          <div className="relative">
            <input
              type="text"
              value={selectedMentee ? getSelectedMenteeName() : menteeSearch}
              onChange={(e) => {
                setMenteeSearch(e.target.value);
                setSelectedMentee('');
                setShowMenteeDropdown(true);
              }}
              onFocus={() => setShowMenteeDropdown(true)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
              placeholder="Search mentees..."
              disabled={!isAdmin}
            />
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
          {showMenteeDropdown && menteeSearch && (
            <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border max-h-60 overflow-auto">
              {filteredMentees.map((mentee) => (
                <button
                  key={mentee.uuid}
                  type="button"
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:outline-none"
                  onClick={() => {
                    setSelectedMentee(mentee.uuid);
                    setMenteeSearch('');
                    setShowMenteeDropdown(false);
                  }}
                >
                  <div className="font-medium">{mentee.name}</div>
                  <div className="text-sm text-gray-500">{mentee.email}</div>
                </button>
              ))}
              {filteredMentees.length === 0 && (
                <div className="px-4 py-2 text-gray-500">No mentees found</div>
              )}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !isAdmin || !selectedMentor || !selectedMentee}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#4CAF50] hover:bg-[#45a049] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4CAF50] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Create Assignment
        </button>
      </form>
    </div>
  );
} 